import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Divider, Grid } from '@material-ui/core';
import { useTranslation } from '../../../src/i18n';
import { TP } from '../../../constants/consts';
import SimpleModal from '../../Modal';
import Title from '../../Title';
import * as languageIcons from '../../../public/images/flags';
import TextArea from '../../Fields/TextArea';
import { getOption } from '../../../utils/common';
import LanguageSelect from '../../LanguageSelect';
import { SaveButtonBlock } from '../SaveButtonBlock';
import ImageTag from '../../ImageTag';

const useStyles = makeStyles(theme => ({
  modal: {
    width: '70vw',
  },
  section: {
    padding: theme.spacing(2),
  },
  langName: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
  },
  text: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  langInfo: {
    fontWeight: 'bold',
    wordBreak: 'break-word',
  },
}));

const EditTranslationModal = ({
  variant,
  isModalOpen,
  bio,
  updateBio,
  addFunc,
  onClose,
  langs,
  modalTitle,
  explication,
  textAreaLabel,
  loading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [content, setContent] = React.useState(bio?.content || '');
  const [lang, setLang] = React.useState(
    bio?.language?.id ? getOption(bio?.language, 'id', 'name', { slug: bio?.language?.lang }, true) : undefined,
  );
  const isEditVariant = variant === 'edit';
  const isAddVariant = variant === 'add';

  const setLangForSaving = langObject => ({ id: langObject?.id, name: langObject?.name, lang: langObject?.slug });

  const saveChanges = () => {
    const bioToChange = { ...bio, content, language: setLangForSaving(lang) };
    if (isEditVariant) {
      updateBio(bioToChange);
    } else {
      addFunc(bioToChange);
    }
  };

  return (
    <SimpleModal isModalOpen={isModalOpen} onClose={onClose} className={classes.modal}>
      <div className={classes.section}>
        <Title size="big" style={{ fontWeight: 'bold', marginRight: '2rem' }}>
          {t(modalTitle[variant])}
        </Title>
        {isEditVariant && (
          <>
            {languageIcons[`lang_${bio?.language?.lang}`] && (
              <ImageTag src={languageIcons[`lang_${bio?.language?.lang}`]} alt={`${bio?.language?.lang} flag`} />
            )}
            <span className={classes.langName}>{lang?.name}</span>
          </>
        )}
      </div>
      <Divider />
      <section className={classes.section}>
        {isAddVariant && (
          <>
            <Title>{t(`${TP}.SELECT_LANGUAGE`)}</Title>
            <Grid container alignItems="center" spacing={2} style={{ width: '100%', marginBottom: '1rem' }}>
              <Grid item sm={2} xs={12}>
                <LanguageSelect langs={langs} name="lang" value={lang} onChange={setLang} />
              </Grid>
              <Grid item sm={8} xs={12}>
                <span className={classes.langInfo}>{t(explication)}</span>
              </Grid>
            </Grid>
          </>
        )}
        <Title>{t(textAreaLabel)}</Title>
        <TextArea text={content} onChange={v => setContent(v)} maxLines={10} minLines={10} className={classes.text} />
        <SaveButtonBlock
          onSaveClick={saveChanges}
          updatedDate={bio?.updatedAt}
          buttonText={t(`${TP}.m_SAVE`)}
          buttonVariant="contained"
          isDisabled={content?.length === 0 || !lang?.value}
          isLoading={loading}
        />
      </section>
    </SimpleModal>
  );
};

EditTranslationModal.propTypes = {
  variant: PropTypes.oneOf(['edit', 'add']),
  isModalOpen: PropTypes.bool,
  bio: PropTypes.object.isRequired,
  addFunc: PropTypes.func.isRequired,
  updateBio: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  langs: PropTypes.array,
  modalTitle: PropTypes.shape({
    add: PropTypes.string,
    edit: PropTypes.string,
  }).isRequired,
  explication: PropTypes.string,
  textAreaLabel: PropTypes.string.isRequired,
  loading: PropTypes.bool,
};

EditTranslationModal.defaultProps = {
  variant: 'add',
  isModalOpen: false,
  langs: [],
  explication: '',
  loading: false,
};

export default memo(EditTranslationModal);
